import { Key } from 'react';
import { atom, atomFamily, selector, selectorFamily } from 'recoil';
import {
  CONTROL_TOWER_PAGE,
  DEFAULT_BOTTOM_CARD_HEIGHT,
  DEFAULT_TOP_CARD_HEIGHT,
  EXPANDED_SIDENAV_WIDTH,
  FLEET_SECTION_ID,
  GPT_DETAIL_PAGE,
  LINKS_PAGE,
  LIVE_STATUS_PAGE,
  NOTIFICATION_EVIDENCE_DETAIL_PAGE,
  NOTIFICATION_EVIDENCES_PAGE,
  NOTIFICATION_PAGE,
  PLACES_SECTION,
  REPORTS_PAGE,
  ROOT_PAGES,
  SECONDARIES_PAGE,
  SETTINGS_PAGE,
  SIDENAV_WIDTH,
  TRACKING_PAGE,
  VIDEO_REQUEST_WINDOW_WIDTH,
  ZONE_SECTION_ID,
} from '../constants/app';
import { PagePermissionName, RightCard } from '../types/app';
import { Workspace } from '../types/workspace';
import { persistedAtom } from '../utils/persistence';
import { accountPermissionsState, accountState } from './account';
import { showConvoyEvaluationsState } from './convoys';
import { leftMapPaddingState } from './map';
import { capturingHasCurrentState, capturingState } from './streaming';

export const appNameState = atom({
  key: 'appName',
  default: 'Gorila',
});

export const isLoggedInState = persistedAtom('app.loggedIn', false);

export const workspacesState = persistedAtom<Workspace[] | undefined>('app.workspaces', undefined);

export const workspaceState = persistedAtom<Workspace | undefined>('app.workspace', undefined);

export const selectWorkspaceState = atom({
  key: 'app.workspace.selection',
  default: false,
});

export const isAllWorkspacesSelect = selector({
  key: 'app.workspace.selection.all',
  get: ({ get }) => {
    const workspace = get(workspaceState);
    if (workspace?.tradeName === 'all') return true;
    return false;
  },
});

export const sidenavIsPinnedState = persistedAtom('app.sidenav.pinned', true);

export const sidenavSectionsState = persistedAtom<Key[]>('app.sidenav.sections', [FLEET_SECTION_ID, ZONE_SECTION_ID]);

export const sidenavIsVisibleState = selector<boolean>({
  key: 'app.sidenav.visible',
  get: ({ get }) => {
    const page = get(pageState);
    return (
      page !== TRACKING_PAGE.id &&
      page !== SECONDARIES_PAGE.id &&
      page !== NOTIFICATION_PAGE.id &&
      page !== LIVE_STATUS_PAGE.id &&
      page !== GPT_DETAIL_PAGE.id
    );
  },
});

export const sidenavWidthState = selectorFamily<number, boolean>({
  key: 'app.sidenav.width',
  get:
    (isHover) =>
    ({ get }) => {
      const sidenavIsVisible = get(sidenavIsVisibleState);

      if (!sidenavIsVisible) return 0;

      const sidenavIsPinned = get(sidenavIsPinnedState);

      if (!sidenavIsPinned && !isHover) {
        return SIDENAV_WIDTH;
      }
      return EXPANDED_SIDENAV_WIDTH;
    },
});

export const activePagesState = selector<PagePermissionName[]>({
  key: 'app.page.active',
  get: ({ get }) => {
    const permissions = get(accountPermissionsState);
    if (!permissions) return [];
    const activePages = Object.entries(permissions)
      .filter(([_, permission]) => permission.enabled)
      .map(([key]) => key as PagePermissionName);
    return activePages;
  },
});

export const pagesState = selector({
  key: 'app.pages',
  get: ({ get }) => {
    const activePages = get(activePagesState);
    const settingsSections = get(settingsSectionsState);
    return ROOT_PAGES.filter(
      (page) =>
        activePages.includes(page.permissionName) ||
        (activePages.includes(SETTINGS_PAGE.permissionName) &&
          page.id === PLACES_SECTION &&
          settingsSections.includes(PLACES_SECTION)),
    );
  },
});

export const pageState = persistedAtom<string>('app.page', '');
export const isPublicPageState = persistedAtom<boolean>('app.page.isPublic', true);

export const isFleetModuleState = atom<boolean>({
  key: 'app.pages.myFleet.module.active',
  default: false,
});

export const canAccessMyFleetState = selector<boolean>({
  key: 'app.pages.myFleet.enabled',
  get: ({ get }) => {
    const isAllWorkspacesSelected = get(isAllWorkspacesSelect);
    if (isAllWorkspacesSelected) return false;
    const activePages = get(activePagesState);
    return activePages.includes(PagePermissionName.myFleet);
  },
});

export const apiParamsState = selector({
  key: 'app.api.params',
  get: ({ get }) => {
    const account = get(accountState);
    const workspace = get(workspaceState);

    if (!account?.client?._id || !workspace) return;

    return {
      clientId: account.client._id,
      subClientId: workspace._id,
    };
  },
});

export const settingsSectionsState = selector({
  key: 'app.settings.sections',
  get: ({ get }) => {
    const permissions = get(accountPermissionsState);
    if (!permissions) return [];
    const availableSections = permissions['settings']['availableSections'];
    if (!availableSections) return [];

    return Object.entries(availableSections)
      .filter(([_, section]) => section.enabled)
      .map(([key]) => key);
  },
});

export const availableActionsState = selector({
  key: 'app.dashboard.actions',
  get: ({ get }) => {
    const permissions = get(accountPermissionsState);
    if (!permissions) return [];
    const availableActions = permissions['dashboard']['availableActions'];
    if (!availableActions) return [];

    return Object.entries(availableActions)
      .filter(([_, action]) => action.enabled)
      .map(([key]) => key);
  },
});

export const showClustersState = selector({
  key: 'app.validations.showCluster',
  get: ({ get }) => {
    const page = get(pageState);
    const showConvoyEvaluations = get(showConvoyEvaluationsState);
    const isCapturing = get(capturingHasCurrentState);
    return (
      page !== TRACKING_PAGE.id &&
      page !== REPORTS_PAGE.id &&
      page !== SECONDARIES_PAGE.id &&
      page !== NOTIFICATION_PAGE.id &&
      page !== LIVE_STATUS_PAGE.id &&
      !showConvoyEvaluations &&
      page !== LINKS_PAGE.id &&
      page !== CONTROL_TOWER_PAGE.id &&
      page !== GPT_DETAIL_PAGE.id &&
      page !== NOTIFICATION_EVIDENCES_PAGE.id &&
      page !== NOTIFICATION_EVIDENCE_DETAIL_PAGE.id &&
      !isCapturing
    );
  },
});

export const floatingCardsState = atom({
  key: 'app.floatingCards.status',
  default: {
    hasList: false,
    hasContent: false,
    listIsExpanded: false,
    contentIsExpanded: false,
    listWidth: 0,
    contentWidth: 0,
  },
});

export const floatingCardsXPositionState = selector<number>({
  key: 'app.floatingCards.position.x',
  get: ({ get }) => {
    const leftMapPadding = get(leftMapPaddingState);
    const isCapturingState = !!get(capturingState).ident;
    return isCapturingState ? leftMapPadding - VIDEO_REQUEST_WINDOW_WIDTH - 12 : leftMapPadding;
  },
});

export const bottomCardHeightState = atomFamily({
  key: 'app.floatingCards.bottom.height',
  default: DEFAULT_BOTTOM_CARD_HEIGHT,
});

export const topCardHeightState = atomFamily({
  key: 'app.floatingCards.top.height',
  default: DEFAULT_TOP_CARD_HEIGHT,
});

export const rigthCardWidthState = atomFamily<number, RightCard>({
  key: 'app.floatingCards.right.width',
  default: 0,
});
